import { faExternalLink } from '@fortawesome/free-solid-svg-icons'
import React, { useContext } from 'react'
import { useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import {
  Avatar,
  Content,
  Data,
  Layout,
  Navigation,
  Responsive,
  theme,
} from 'ui'
import { truncate } from 'utils'
import { useResourceCategories } from '../../../queries'

const { Table, Cell, Row, Header } = Data

export const ResourceCategoryListView: React.FC = () => {
  const { useDisplayMode } = useContext(Responsive.DrawerManagerContext)
  useDisplayMode('expanded')
  const navigate = useNavigate()
  const { error, isLoading: loading, data } = useResourceCategories()
  const handleNewResource: React.MouseEventHandler = (e) => {
    e.preventDefault()
    navigate('/admin/resources/categories/new')
  }

  const categories = data ?? []

  return (
    <Layout.PaddedContent className="pb-24 md:pb-32">
      <Helmet>
        <title>Categories - MCSS Pro Connect</title>
      </Helmet>
      <div
        className="flex-col flex w-full"
        style={{ opacity: loading ? 0.5 : 1 }}
      >
        <Content.Text className="pt-2">
          <Navigation.ReturnLink to={`/admin/resources`}>
            Return to Resources
          </Navigation.ReturnLink>
        </Content.Text>
        <div className="flex-col flex sm:flex-row flex">
          <div className="w-full sm:w-1/2">
            <Content.Heading className="mt-4 mb-2">Categories</Content.Heading>
          </div>
          <div className="ml-0 sm:ml-auto mt-3 sm:mt-auto w-full sm:w-1/2 md:w-1/3">
            <div className="w-full">
              <button
                onClick={handleNewResource}
                className="w-full px-4 py-2 bg-teal-600 text-white font-medium rounded-md hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 transition-colors duration-200"
              >
                <span>Create Category</span>
              </button>
            </div>
          </div>
        </div>
        <div className="flex-col flex sm:flex-row flex">
          <div className="w-full">
            <Table>
              <Row>
                <Header width="40%" className="!text-secondary">
                  Title
                </Header>
                <Header width="30%" className="!text-secondary">
                  Content
                </Header>
                <Header width="10%" className="!text-secondary">
                  &nbsp;
                </Header>
              </Row>
              {categories ? (
                categories.map((category) => (
                  <Row key={category.id}>
                    <Cell width="40%">
                      <div className="flex items-center space-x-3">
                        <Avatar.Simple
                          name={category.name}
                          maxInitials={1}
                          color={theme.colorPrimary}
                          size={36}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-gray-900">
                            {truncate(category.name ?? '', 50)}
                          </div>
                        </div>
                      </div>
                    </Cell>
                    <Cell width="30%">{truncate(category.slug ?? '', 80)}</Cell>
                    <Cell width="10%">
                      <Navigation.IconLink
                        icon={faExternalLink}
                        to={`/admin/resources/categories/show/${category.id}`}
                      />
                    </Cell>
                  </Row>
                ))
              ) : error ? (
                <Row>
                  <Cell>
                    <div className="text-red-600 p-4 bg-red-50 rounded-md">
                      {error instanceof Error
                        ? error.message
                        : 'There was a problem fetching categories.'}
                    </div>
                  </Cell>
                </Row>
              ) : (
                <Row>
                  <Cell>No results found.</Cell>
                </Row>
              )}
            </Table>
          </div>
        </div>
      </div>
    </Layout.PaddedContent>
  )
}
